/**
 * Carga previsualización JPG/WebP con hit IndexedDB preview + web image loader.
 *
 * Importante:
 * - cornerstone-web-image-loader solo registra esquemas http/https (no "blob:").
 * - createImage debe ejecutarse con external.cornerstone seteado; si no, image.render
 *   queda undefined y el viewport se ve blanco sin error de tags DICOM.
 * - Un solo fetch de red alimenta decode + IDB (sin segundo GET).
 */
import cornerstone from 'cornerstone-core'
import * as cornerstoneWebImageLoader from 'cornerstone-web-image-loader'
import {
  DICOM_OFFLINE_CACHE_ENABLED,
  getCachedPreview,
  toBlob,
} from '../db/dicomCache'
import { enqueuePutPreview } from '../db/dicomCacheWriteQueue'
import { sopFromManifestInstance, fallbackCacheKey } from './loadDicomInstanceWithCache'
import { ensureImageRender } from './ensureImageRender'

let webLoaderRoot = null

function getWebLoaderRoot() {
  if (webLoaderRoot && webLoaderRoot.external && webLoaderRoot.external.cornerstone) {
    return webLoaderRoot
  }
  try {
    const root =
      cornerstoneWebImageLoader &&
      (cornerstoneWebImageLoader.external
        ? cornerstoneWebImageLoader
        : cornerstoneWebImageLoader.default || cornerstoneWebImageLoader)
    if (root && root.external) {
      root.external.cornerstone = cornerstone
    }
    webLoaderRoot = root
  } catch (e) {
    webLoaderRoot = cornerstoneWebImageLoader
    try {
      if (webLoaderRoot && webLoaderRoot.external) {
        webLoaderRoot.external.cornerstone = cornerstone
      }
    } catch (e2) {
      /* ignore */
    }
  }
  return webLoaderRoot
}

function ensureWebImageRender(image) {
  return ensureImageRender(image, { renderKind: 'preview' })
}

/**
 * Decodifica bytes vía arrayBufferToImage + createImage.
 * @param {Blob|ArrayBuffer} data
 * @param {string} imageId
 * @returns {Promise<object>}
 */
async function imageFromBytes(data, imageId) {
  const root = getWebLoaderRoot()
  const arrayBufferToImage =
    (root && root.arrayBufferToImage) ||
    (root && root.default && root.default.arrayBufferToImage)
  const createImage =
    (root && root.createImage) || (root && root.default && root.default.createImage)

  if (!arrayBufferToImage || !createImage) {
    throw new Error('cornerstone-web-image-loader sin arrayBufferToImage/createImage')
  }

  let ab = data
  if (data instanceof Blob) {
    ab = await data.arrayBuffer()
  }
  const htmlImage = await arrayBufferToImage(ab)
  if (!htmlImage || !htmlImage.naturalWidth) {
    throw new Error('Preview decode: imagen 0×0')
  }
  let image = createImage(htmlImage, imageId)
  image = ensureWebImageRender(image)
  return image
}

/**
 * Publica image en cache de Cornerstone para reuso por imageId (sin re-fetch).
 * @param {string} imageId
 * @param {object} image
 */
function putImageInCornerstoneCache(imageId, image) {
  if (!imageId || !image || !cornerstone.imageCache) return
  try {
    if (typeof cornerstone.imageCache.getImageLoadObject === 'function') {
      const existing = cornerstone.imageCache.getImageLoadObject(imageId)
      if (existing) return
    }
    if (typeof cornerstone.imageCache.putImageLoadObject === 'function') {
      cornerstone.imageCache.putImageLoadObject(imageId, {
        promise: Promise.resolve(image),
        cancelFn: undefined,
      })
    }
  } catch (e) {
    /* ignore: cache opcional */
  }
}

/**
 * Hit de imageCache de Cornerstone para imageId https de preview.
 * @param {string} imageId
 * @returns {Promise<object|null>}
 */
async function tryLoadFromImageCache(imageId) {
  if (!imageId || !cornerstone.imageCache) return null
  try {
    if (typeof cornerstone.imageCache.getImageLoadObject !== 'function') return null
    const loadObject = cornerstone.imageCache.getImageLoadObject(imageId)
    if (!loadObject) return null
    const image = await cornerstone.loadAndCacheImage(imageId)
    return ensureWebImageRender(image)
  } catch (e) {
    return null
  }
}

/**
 * @param {object} opts
 * @param {string} opts.previewUrl
 * @param {object} [opts.inst]
 * @param {string} [opts.studyInstanceUID]
 * @param {string} [opts.sopInstanceUID]
 * @param {boolean} [opts.skipIdbRead] — no leer preview_cache (política offlineCache=0)
 * @param {boolean} [opts.skipIdbWrite] — no encolar put (política offlineCache=0)
 */
export async function loadPreviewInstance(opts) {
  const previewUrl = opts && opts.previewUrl ? String(opts.previewUrl).trim() : ''
  if (!previewUrl) throw new Error('previewUrl vacío')

  const studyUid = (opts && opts.studyInstanceUID) || ''
  const skipIdbRead = !!(opts && opts.skipIdbRead)
  const skipIdbWrite = !!(opts && opts.skipIdbWrite)
  const sop =
    (opts && opts.sopInstanceUID) ||
    sopFromManifestInstance(opts && opts.inst) ||
    fallbackCacheKey(previewUrl, opts && opts.inst)

  const isHttp = /^https?:\/\//i.test(previewUrl)
  const imageIdHttp = isHttp ? previewUrl : null
  const imageIdPreview = isHttp
    ? previewUrl
    : `preview://${encodeURIComponent(String(sop || 'img'))}`

  // 0) Cache en memoria de Cornerstone (misma sesión / scroll)
  if (imageIdHttp) {
    const cached = await tryLoadFromImageCache(imageIdHttp)
    if (cached) {
      return {
        image: cached,
        imageId: imageIdHttp,
        sopInstanceUID: String(sop),
        fromCache: true,
        renderKind: 'preview',
        contentType: 'image/*',
      }
    }
  }

  // 1) IDB hit → decode bytes locales (un solo stream: blob ya en cache)
  if (DICOM_OFFLINE_CACHE_ENABLED && sop && !skipIdbRead) {
    try {
      const row = await getCachedPreview(String(sop))
      if (row && row.status === 'complete' && row.blob) {
        const blob = row.blob instanceof Blob ? row.blob : toBlob(row.blob)
        if (blob && blob.size > 0) {
          const imageId = imageIdPreview
          let image = await imageFromBytes(blob, imageId)
          putImageInCornerstoneCache(imageId, image)
          return {
            image,
            imageId,
            sopInstanceUID: String(sop),
            fromCache: true,
            renderKind: 'preview',
            contentType: row.contentType || 'image/jpeg',
          }
        }
      }
    } catch (e) {
      console.warn('loadPreviewInstance: cache hit failed, network', e)
    }
  }

  // 2) Un solo fetch → decode + encolar IDB (sin segundo GET)
  getWebLoaderRoot()
  const res = await fetch(previewUrl, {
    method: 'GET',
    credentials: 'omit',
  })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} al descargar preview`)
  }
  const contentType = res.headers.get('Content-Type') || 'image/jpeg'
  const buf = await res.arrayBuffer()
  if (!buf || buf.byteLength === 0) {
    throw new Error('Preview vacío (0 bytes)')
  }

  if (DICOM_OFFLINE_CACHE_ENABLED && sop && !skipIdbWrite) {
    try {
      enqueuePutPreview({
        sopInstanceUID: String(sop),
        studyInstanceUID: studyUid,
        blob: new Blob([buf], { type: contentType }),
        contentType,
      })
    } catch (e) {
      /* ignore write-queue errors */
    }
  }

  const imageId = imageIdPreview
  let image = await imageFromBytes(buf, imageId)
  putImageInCornerstoneCache(imageId, image)

  return {
    image,
    imageId,
    sopInstanceUID: String(sop),
    fromCache: false,
    renderKind: 'preview',
    contentType,
  }
}

/**
 * Rehidrata preview por SOP desde IDB (viewport).
 * @param {string} sopInstanceUID
 */
export async function loadPreviewFromCacheBySop(sopInstanceUID) {
  if (!DICOM_OFFLINE_CACHE_ENABLED || !sopInstanceUID) return null
  const row = await getCachedPreview(String(sopInstanceUID))
  if (!row || row.status !== 'complete' || !row.blob) return null
  const blob = row.blob instanceof Blob ? row.blob : toBlob(row.blob)
  if (!blob) return null
  const imageId = `preview://${encodeURIComponent(String(sopInstanceUID))}`
  try {
    const image = await imageFromBytes(blob, imageId)
    putImageInCornerstoneCache(imageId, image)
    return {
      image,
      imageId,
      sopInstanceUID: String(sopInstanceUID),
      fromCache: true,
      renderKind: 'preview',
    }
  } catch (e) {
    console.warn('loadPreviewFromCacheBySop failed', e)
    return null
  }
}

export function ensureWebImageLoader() {
  getWebLoaderRoot()
}
